/**
 * 💰 Money Value Object
 *
 * Immutable value object يمثّل مبلغ مالي بأمان.
 *
 * Why?
 * ────
 * - يمنع floating point bugs (0.1 + 0.2 !== 0.3)
 * - يمنع إنشاء أرقام سالبة بالخطأ
 * - Type-safe (Money + Money فقط، لا يقبل number)
 * - Currency-aware (لا يجمع SAR + USD)
 *
 * Pattern: Value Object (DDD)
 *   - Immutable
 *   - Equality by value
 *   - Self-validating
 */

export type Currency = 'SAR' | 'USD' | 'AED';

export class Money {
  // نخزّن بـ "halalas" (1 SAR = 100 halala) كـ integer
  // → لا floating point errors أبداً
  private readonly halalas: number;
  private readonly currency: Currency;

  private constructor(halalas: number, currency: Currency) {
    this.halalas = Math.round(halalas);
    this.currency = currency;
  }

  /**
   * إنشاء Money من decimal (e.g., 100.50 SAR)
   */
  static of(amount: number, currency: Currency = 'SAR'): Money {
    if (!Number.isFinite(amount)) {
      throw new Error(`Money: amount لازم يكون رقم صالح, got ${amount}`);
    }
    if (amount < 0) {
      throw new Error(`Money: amount لا يمكن أن يكون سالباً, got ${amount}`);
    }
    if (amount > 1_000_000_000) {
      throw new Error(`Money: amount كبير جداً (max: 1B), got ${amount}`);
    }
    return new Money(amount * 100, currency);
  }

  /**
   * Money = 0
   */
  static zero(currency: Currency = 'SAR'): Money {
    return new Money(0, currency);
  }

  /**
   * إنشاء Money من halalas (الوحدة الصغرى - integer)
   * يستخدم عند القراءة من DB
   */
  static fromHalalas(halalas: number, currency: Currency = 'SAR'): Money {
    if (!Number.isFinite(halalas)) {
      throw new Error(`Money.fromHalalas: invalid value ${halalas}`);
    }
    return new Money(halalas, currency);
  }

  /**
   * إنشاء Money من SAR (alias لـ Money.of للوضوح)
   */
  static fromSAR(amount: number): Money {
    return Money.of(amount, 'SAR');
  }

  /**
   * Parse من string (e.g., "100.50")
   */
  static parse(value: string, currency: Currency = 'SAR'): Money {
    const parsed = parseFloat(value);
    if (Number.isNaN(parsed)) {
      throw new Error(`Money: تعذّر تحليل "${value}"`);
    }
    return Money.of(parsed, currency);
  }

  /**
   * القيمة كـ decimal (للعرض)
   */
  toNumber(): number {
    return this.halalas / 100;
  }

  /**
   * القيمة بالـ halalas (للحفظ في DB)
   */
  toHalalas(): number {
    return this.halalas;
  }

  /**
   * القيمة بالريال (alias لـ toNumber للوضوح)
   */
  toSAR(): number {
    return this.halalas / 100;
  }

  getCurrency(): Currency {
    return this.currency;
  }

  /**
   * Format للعرض (e.g., "100.50 ر.س")
   */
  format(locale: string = 'ar-SA'): string {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return formatter.format(this.toNumber());
  }

  // ─── Operations (immutable — ترجع Money جديد) ───

  add(other: Money): Money {
    this.assertSameCurrency(other);
    return new Money(this.halalas + other.halalas, this.currency);
  }

  subtract(other: Money): Money {
    this.assertSameCurrency(other);
    const result = this.halalas - other.halalas;
    if (result < 0) {
      throw new Error(`Money: لا يمكن الطرح — النتيجة سالبة`);
    }
    return new Money(result, this.currency);
  }

  multiply(factor: number): Money {
    if (!Number.isFinite(factor) || factor < 0) {
      throw new Error(`Money: factor غير صالح`);
    }
    return new Money(this.halalas * factor, this.currency);
  }

  /**
   * نسبة مئوية (e.g., commission 15%)
   */
  percentage(percent: number): Money {
    if (percent < 0 || percent > 100) {
      throw new Error(`Money: percent لازم بين 0 و 100, got ${percent}`);
    }
    return new Money((this.halalas * percent) / 100, this.currency);
  }

  // ─── Comparison ───

  equals(other: Money): boolean {
    return this.halalas === other.halalas && this.currency === other.currency;
  }

  greaterThan(other: Money): boolean {
    this.assertSameCurrency(other);
    return this.halalas > other.halalas;
  }

  greaterThanOrEqual(other: Money): boolean {
    this.assertSameCurrency(other);
    return this.halalas >= other.halalas;
  }

  lessThan(other: Money): boolean {
    this.assertSameCurrency(other);
    return this.halalas < other.halalas;
  }

  isZero(): boolean {
    return this.halalas === 0;
  }

  // ─── Private ───

  private assertSameCurrency(other: Money): void {
    if (this.currency !== other.currency) {
      throw new Error(
        `Money: عملات مختلفة (${this.currency} vs ${other.currency})`
      );
    }
  }
}
